import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Button,
    Box,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    Paper,
    Pagination,
    Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axios from '../../utils/axios';

const ExamList = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const isMyExams = location.pathname === '/my-exams';
    
    // Pagination and search state
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [totalExams, setTotalExams] = useState(0);

    useEffect(() => {
        const fetchExams = async () => {
            try {
                setLoading(true);
                let endpoint = '';
                let response = null;
                
                if (isMyExams) {
                    endpoint = '/api/exams/user';
                    response = await axios.get(endpoint);
                    setExams(response.data);
                } else {
                    endpoint = `/api/exams/published?page=${page}&limit=20&search=${searchTerm}`;
                    response = await axios.get(endpoint);
                    setExams(response.data.exams);
                    setTotalPages(response.data.pages);
                    setTotalExams(response.data.totalExams);
                }
            } catch (err) {
                setError(err.response?.data?.message || 'Error fetching exams');
            } finally {
                setLoading(false);
            }
        };

        fetchExams();
    }, [isMyExams, page, searchTerm]);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setSearchTerm(searchInput);
        setPage(1); // Reset to first page when searching
    };

    if (loading && page === 1) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container>
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom>
                {isMyExams ? 'My Exams' : 'Available Exams'}
            </Typography>

            {!isMyExams && (
                <Box mb={3}>
                    <form onSubmit={handleSearch}>
                        <TextField
                            fullWidth
                            placeholder="Search by title or category..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button 
                                            type="submit" 
                                            variant="contained"
                                            disabled={loading}
                                        >
                                            Search
                                        </Button>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </form>
                </Box>
            )}

            {!isMyExams && exams.length > 0 && (
                <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {exams.length} of {totalExams} exams
                    </Typography>
                </Box>
            )}

            {exams.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography>
                        {searchTerm ? `No exams found matching "${searchTerm}"` : 'No exams available'}
                    </Typography>
                </Paper>
            ) : (
                <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                    {exams.map((exam) => (
                        <Paper key={exam._id} sx={{ mb: 2 }}>
                            <ListItem>
                                <Box sx={{ width: '100%', pr: 10 }}>
                                    <Typography variant="h6">{exam.title || exam.name}</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary" 
                                            paragraph 
                                            component="div"
                                        >
                                            {exam.description}
                                        </Typography>
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                            <Chip 
                                                label={`${exam.numberOfQuestions} Questions`} 
                                                size="small"
                                            />
                                            {exam.category && (
                                                <Chip 
                                                    label={exam.category} 
                                                    size="small" 
                                                    color="primary" 
                                                />
                                            )}
                                            {exam.price > 0 ? (
                                                <Chip 
                                                    label={`£${Number(exam.price).toFixed(2)}`} 
                                                    size="small" 
                                                    color="secondary"
                                                />
                                            ) : (
                                                <Chip 
                                                    label="Free" 
                                                    size="small" 
                                                    color="success"
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                                <ListItemSecondaryAction>
                                    <Button
                                        variant="outlined"
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={() => navigate(`/exams/${exam._id}`)}
                                    >
                                        {isMyExams ? 'Continue' : 'View Details'}
                                    </Button>
                                </ListItemSecondaryAction>
                            </ListItem>
                        </Paper>
                    ))}
                </List>
            )}

            {!isMyExams && totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={4}>
                    <Pagination 
                        count={totalPages} 
                        page={page} 
                        onChange={handlePageChange} 
                        color="primary" 
                        size="large"
                        disabled={loading}
                    />
                </Box>
            )}
        </Container>
    );
};

export default ExamList; 